import { IconCrossSmall } from 'central-icons/IconCrossSmall';
import type React from 'react';

import { Popover, type PopoverProps, Button as UiButton } from '@revelio/ui';

import styles from './tutorial-popup.module.css';
import terminalIntroductionThumbnail from './tutorial-thumbnails/01-terminal-introduction.png';
import analyticsThumbnail from './tutorial-thumbnails/02-analytics.png';
import discoveryThumbnail from './tutorial-thumbnails/03-discovery.png';
import applicationsThumbnail from './tutorial-thumbnails/04-applications.png';
import reportsThumbnail from './tutorial-thumbnails/05-reports.png';
import dataThumbnail from './tutorial-thumbnails/06-data.png';

export type VideoData = {
  id: string;
  name: string;
  description: string;
  duration: string;
  thumbnail: string;
  mediaId: string;
  paths: string[];
};

export interface TutorialVideoGroup {
  label: string;
  videos: VideoData[];
}

export const videoMetadata: TutorialVideoGroup[] = [
  {
    label: 'Terminal',
    videos: [
      {
        name: 'Overview',
        description: 'Overview of the Revelio Labs Terminal',
        duration: '1 min',
        thumbnail: terminalIntroductionThumbnail,
        mediaId: 'ncvp6ycty5',
        id: '01-terminal-introduction',
        paths: ['/'],
      },
      {
        name: 'Analytics',
        description:
          'Explore workforce data through multiple lenses: Company, Industry, Geography, and Role.',
        duration: '2 mins',
        thumbnail: analyticsThumbnail,
        mediaId: 'hsmqlgn4fg',
        id: '02-analytics',
        paths: [
          '/company/compositions',
          '/company/transitions',
          '/company/postings',
          '/company/pay',
          '/company/diversity',
          '/company/sentiment',
          '/industry/compositions',
          '/industry/transitions',
          '/industry/postings',
          '/industry/pay',
          '/industry/market-conditions',
          '/industry/sentiment',
          '/geography/compositions',
          '/geography/transitions',
          '/geography/postings',
          '/geography/pay',
          '/geography/market-conditions',
          '/geography/sentiment',
          '/role/compositions',
          '/role/transitions',
          '/role/postings',
          '/role/pay',
          '/role/market-conditions',
          '/role/sentiment',
        ],
      },
      {
        name: 'Discovery',
        description:
          'Search our underlying database directly and drill down into individuals, companies, geographies, roles, or job postings.',
        duration: '1 min',
        thumbnail: discoveryThumbnail,
        mediaId: 'wrh7cxhgev',
        id: '03-discovery',
        paths: [
          '/talent-discovery',
          '/company/screener',
          '/geography/screener',
          '/role/screener',
          '/posting-discovery',
        ],
      },
      {
        name: 'Applications',
        description: 'Upload your own data and enrich it using our models',
        duration: '1 min',
        thumbnail: applicationsThumbnail,
        mediaId: 'njrri4vgdv',
        id: '04-applications',
        paths: ['/deliverables/data-enrichment', '/resume-enrichment'],
      },
      {
        name: 'Reports',
        description:
          'Get access to preconfigured reports that answer common workforce questions, from AI exposure and adoption, to market conditions across geographies, to compensation and organizational strategy',
        duration: '1 min',
        thumbnail: reportsThumbnail,
        mediaId: 'gmqfm7qeac',
        id: '05-reports',
        paths: [
          '/report-builder/report_type',
          '/report-builder',
          '/reports',
          '/sample-report',
        ],
      },
      {
        name: 'Data',
        description:
          'Create custom datasets from the same underlying data that powers the terminal',
        duration: '1 min',
        thumbnail: dataThumbnail,
        mediaId: 'qj7dble528',
        id: '06-data',
        paths: ['/data-builder', '/data-builder-status'],
      },
    ],
  },
];

/** The video the modal falls back to when nothing specific has been picked. */
export const DEFAULT_TUTORIAL_VIDEO_ID: string | null =
  videoMetadata[0]?.videos[0]?.id ?? null;

/**
 * A video claims a route when the route IS one of its paths or sits UNDER one
 * ('/data-builder' covers '/data-builder/choose', '/reports' covers
 * '/reports/:id'). Exact matching left every nested step page — the whole of
 * Data Builder and Report Builder — without a tutorial. '/' is the landing
 * page alone and never a prefix.
 */
const videoCoversPath = (videoPath: string, pathname: string): boolean => {
  if (videoPath === '/') return pathname === '/';
  const path = videoPath.replace(/\/$/, '');
  return pathname === path || pathname.startsWith(`${path}/`);
};

export const tutorialVideoCoversPath = (
  video: VideoData,
  pathname: string
): boolean => video.paths.some((path) => videoCoversPath(path, pathname));

export const findTutorialVideoForPath = (
  pathname: string
): VideoData | null => {
  for (const group of videoMetadata) {
    const video = group.videos.find((candidate) =>
      tutorialVideoCoversPath(candidate, pathname)
    );
    if (video) return video;
  }
  return null;
};

export const findTutorialVideoById = (
  id: string | null | undefined
): VideoData | null => {
  if (!id) return null;
  for (const group of videoMetadata) {
    const video = group.videos.find((candidate) => candidate.id === id);
    if (video) return video;
  }
  return null;
};

export interface TutorialProps
  extends Pick<PopoverProps, 'placement' | 'sideOffset'> {
  isOpen: boolean;
  onClose: () => void;
  // The element the popup is positioned against (the Tutorials rail button).
  // Deliberately an anchor and not a trigger: a click on the rail button opens
  // the Tutorials modal, never this popup.
  children: React.ReactElement;
  videoData: VideoData | null;
  onWatch: () => void;
  onContactSales: () => void;
  onSeen?: () => void;
  /** Appends the "Contact Sales to get started." call to action to the
   *  description — set when the viewer is not entitled to this video's
   *  section, so the popup upsells instead of pointing at a dead route. */
  showContactSales?: boolean;
}

export const Tutorial = ({
  isOpen,
  onClose,
  children,
  videoData,
  onWatch,
  onContactSales,
  placement = 'right-end',
  sideOffset = 0,
  onSeen,
  showContactSales = false,
}: TutorialProps) => {
  if (!videoData) return children;

  return (
    <Popover
      anchor={children}
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      placement={placement}
      sideOffset={sideOffset}
      arrow
      className={styles.popupContent}
      // Focus stays where the user left it: the popup opens on its own, and
      // pulling focus in — or restoring it to the rail on close — would take
      // it from whatever the user is doing.
      onOpenAutoFocus={(event) => event.preventDefault()}
      onCloseAutoFocus={(event) => event.preventDefault()}
      // It dismisses on its own terms only — the close button, a click on the
      // rail button or the auto-open timer. Chakra never blur-closed this one either, since
      // focus never entered it.
      onInteractOutside={(event) => event.preventDefault()}
    >
      <div className={styles.popupBody}>
        <div className={styles.titleRow}>
          <span className={styles.popupTitle}>Tutorial</span>
        </div>
        <button
          type="button"
          className={styles.thumbnailButton}
          onMouseEnter={onSeen}
          onClick={() => {
            onSeen?.();
            onWatch();
          }}
        >
          <img
            className={styles.thumbnailImage}
            src={videoData.thumbnail}
            alt="video thumbnail"
          />
        </button>
        <div className={styles.textBlock}>
          <p className={styles.videoName}>{videoData.name}</p>
          <p className={styles.videoDescription}>
            {videoData.description}
            {showContactSales && (
              <span>
                {` `}
                <UiButton
                  variant="link"
                  className={styles.contactSalesLink}
                  onClick={onContactSales}
                >
                  Contact Sales
                </UiButton>{' '}
                to get started.
              </span>
            )}
          </p>
        </div>
        <UiButton
          color="primary"
          fullWidth
          onMouseEnter={onSeen}
          onClick={() => {
            onSeen?.();
            onWatch();
          }}
        >
          Watch tutorial
          <span className={styles.watchDuration}>{videoData.duration}</span>
        </UiButton>
      </div>
      <button
        type="button"
        aria-label="Close"
        data-testid="tutorial-close-button"
        className={styles.closeButton}
        onClick={onClose}
      >
        <IconCrossSmall size={12} ariaHidden={true} />
      </button>
    </Popover>
  );
};
